/**
 * Class-scan rows on Standout Review. These are entries on the student
 * artwork map, not standout moments. Share writes isShared true; Unshare
 * writes false. Don't share writes `dontShare` and the optional reason,
 * never `isHidden`, so the scan stays here and in the rest of Jarvis; Allow
 * share turns it back off. Hidden scans and scans with no class session are
 * not passed in.
 */
import { CameraOutlined } from "@ant-design/icons"
import { App, Tag, Typography } from "antd"
import dayjs from "dayjs"
import { sortBy } from "lodash"
import { type ReactNode, useCallback, useMemo, useState } from "react"

import Firebase from "#classes/Firebase"
import ArtworkEditableThumb, {
  ARTWORK_EDIT_SOURCE,
} from "#components/ArtworkTiles/ArtworkEditableThumb"
import ClickableStudentName from "#components/ClickableStudentName"
import {
  buildArtworkDontShareUpdate,
  buildArtworkSharedUpdate,
  isArtworkDontShare,
  isClassImageShared,
  type ReviewClassImage,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { invariant } from "#customTypes/types"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { STANDARD_DATE_TIME_FORMAT } from "#utils/dateHelpers"

import DontShareReasonModal, {
  DontShareReasonText,
} from "./DontShareReasonModal"
import { useFamilyDelivery } from "./familyDeliveryContext"
import { ReviewShareSwitch } from "./MomentShareSwitch"
import {
  REVIEW_BLOCK,
  REVIEW_MUTED,
  reviewEdge,
  reviewEdgeColor,
  ReviewField,
  reviewHalfStyle,
  reviewStatusStyle,
  type SentFamilyNote,
  SentFamilyNoteList,
} from "./reviewCardLayout"
import {
  ItemAttribution,
  itemBylineRowStyle,
  ItemKindTitle,
} from "./reviewItemBody"
import { ShareChoice } from "./ShareChoice"
import StandoutFeedRow, {
  FEED_DONT_SHARE_PILL,
  FEED_PENDING_PILL,
  FEED_ROLE_PILL,
  FEED_SCAN_ICON_BG,
  FEED_SHARED_PILL,
  FeedShareCheckbox,
  FeedTypeIcon,
} from "./StandoutFeedRow"
import { feedItemSourceLabel } from "./standoutReviewCardHelpers"
import { itemsSharedMessage } from "./standoutSessionGroups"
import { useSingleFlight } from "./useSingleFlight"

const { Text } = Typography

/** Where the scan was captured. Artwork stores that on `capturedBy`. */
const ImageSourceTag = ({ capturedBy }: { capturedBy?: string | null }) => {
  if (!capturedBy) return null
  return (
    <Tag data-testid="sr-image-source" data-source={capturedBy}>
      {feedItemSourceLabel(capturedBy)}
    </Tag>
  )
}

export interface ReviewItemSelection {
  checked: boolean
  onChange: (checked: boolean) => void
}

export const ClassImageReviewRow = ({
  row,
  selection,
  variant = "card",
  authorName,
  roleLabel,
  identity,
  asItem,
  imageSize,
}: {
  row: ReviewClassImage
  selection?: ReviewItemSelection
  variant?: "card" | "feed"
  authorName?: string
  roleLabel?: string
  /** Set when this row is the whole card, not a line under a student header. */
  identity?: {
    student?: StudentConstructor | null
    levelLabel?: string | null
    className: string
    teacherName: string
    bylineName: string
    bylineWhen: string
    sentNotes?: readonly SentFamilyNote[]
  }
  /** A photo stacked in a session card. No class label and no filename. */
  asItem?: {
    bylineName: string
    bylineWhen: string
  }
  /**
   * Shared photos on a session card use 100. The thumb still opens the
   * full-screen lightbox, and crop and rotate stay available.
   */
  imageSize?: number
}) => {
  const theme = useThemeContext()
  const { currentUserId } = useUserContext()
  const { message } = App.useApp()
  const delivery = useFamilyDelivery()
  const canShare = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )
  const { pending: isSharing, run: runShare } = useSingleFlight()
  const { pending: isUpdatingDontShare, run: runDontShare } = useSingleFlight()
  const [dontShareOpen, setDontShareOpen] = useState(false)
  const shared = isClassImageShared(row.artwork)
  const dontShare = isArtworkDontShare(row.artwork)
  const { artwork } = row
  const feed = variant === "feed"

  const writeShared = useCallback(
    (nextShared: boolean) =>
      runShare(async () => {
        invariant(currentUserId != null, "Current user id is not defined!")
        const update = buildArtworkSharedUpdate(
          { [artwork.id]: artwork },
          artwork.id,
          nextShared,
          currentUserId,
          nextShared ? new Date().toISOString() : undefined
        )
        if (!update) return
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STUDENTS,
            row.studentId,
            update
          )
          if (nextShared) {
            delivery.reset(row.studentId, artwork.classSessionId)
          } else {
            delivery.unshared(row.studentId, artwork.classSessionId, {
              kind: "image",
              studentId: row.studentId,
              artworkId: artwork.id,
            })
          }
          void message.success(
            itemsSharedMessage(1, nextShared ? "shared" : "unshared")
          )
        } catch {
          void message.error("Could not update image sharing. Try again.")
        }
      }),
    [artwork, currentUserId, delivery, message, row.studentId, runShare]
  )

  const handleShareToggle = useCallback(() => {
    void writeShared(!shared)
  }, [shared, writeShared])

  const writeDontShare = useCallback(
    (nextDontShare: boolean, reason?: string) =>
      runDontShare(async () => {
        const update = buildArtworkDontShareUpdate(
          { [artwork.id]: artwork },
          artwork.id,
          nextDontShare,
          reason
        )
        if (!update) return
        try {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STUDENTS,
            row.studentId,
            update
          )
          if (nextDontShare) {
            delivery.unshared(row.studentId, artwork.classSessionId, {
              kind: "image",
              studentId: row.studentId,
              artworkId: artwork.id,
            })
          }
          setDontShareOpen(false)
          void message.success(
            nextDontShare
              ? "Photo will not be sent to parents."
              : "Photo can be shared again."
          )
        } catch {
          void message.error("Could not update this photo. Try again.")
        }
      }),
    [artwork, delivery, message, row.studentId, runDontShare]
  )

  const handleDontShareClick = useCallback(() => {
    if (dontShare) {
      void writeDontShare(false)
      return
    }
    setDontShareOpen(true)
  }, [dontShare, writeDontShare])

  const dontShareModal = (
    <DontShareReasonModal
      open={dontShareOpen}
      subject="photo"
      pending={isUpdatingDontShare}
      onConfirm={(reason) => void writeDontShare(true, reason)}
      onCancel={() => setDontShareOpen(false)}
    />
  )

  const checkbox =
    feed && canShare && (shared || selection) ? (
      <FeedShareCheckbox
        testId="sr-class-image-checkbox"
        shared={shared}
        checked={!!selection?.checked}
        disabled={isSharing}
        ariaLabel={shared ? "Unshare image" : "Select image"}
        dataAttrs={{ "data-artwork-id": artwork.id }}
        onChange={(checked) => {
          if (shared) {
            if (!checked) void writeShared(false)
            return
          }
          selection?.onChange(checked)
        }}
      />
    ) : null

  const shareSwitch =
    canShare && !feed ? (
      <ReviewShareSwitch
        testId="sr-class-image-share"
        artworkId={artwork.id}
        shared={shared}
        loading={isSharing}
        disabled={dontShare}
        onToggle={handleShareToggle}
      />
    ) : null

  const sourceTag = artwork.capturedBy ? (
    <span
      data-testid="sr-image-source"
      data-source={artwork.capturedBy}
      style={{
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 11,
        lineHeight: "16px",
        color: "#fff",
        background: "rgba(0, 0, 0, 0.65)",
      }}
    >
      {feedItemSourceLabel(artwork.capturedBy)}
    </span>
  ) : null

  const image = (
    size: number,
    hideFileName = false,
    showDontShare = true,
    tags: ReactNode = null
  ) => (
    <ArtworkEditableThumb
      studentId={row.studentId}
      artworkId={artwork.id}
      artwork={artwork}
      source={ARTWORK_EDIT_SOURCE.STANDOUT_CLASS_IMAGE}
      size={size}
      borderRadius={8}
      alt={hideFileName ? "Student artwork" : artwork.filename || "Class image"}
      caption={
        hideFileName
          ? "Student artwork"
          : artwork.title?.trim() || artwork.filename || "Class scan"
      }
      viewLabel="Open class scan"
      testId="sr-class-image-open"
      imageTestId="sr-class-image-img"
      showTileActions={true}
      onDontShare={showDontShare && canShare ? handleDontShareClick : undefined}
      dontSharePending={isUpdatingDontShare}
      imageTags={tags}
    />
  )

  const edge = reviewEdge(dontShare, shared)
  const edgeColor = reviewEdgeColor(edge, {
    green: theme.eric.green,
    red: theme.eric.red,
    gray: REVIEW_MUTED,
  })

  if (asItem) {
    return (
      <div
        data-testid="sr-class-image"
        data-artwork-id={artwork.id}
        data-student-id={row.studentId}
        data-shared={shared ? "true" : "false"}
        data-dont-share={dontShare ? "true" : "false"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: 12,
          borderRadius: 8,
          border: `1px solid ${edgeColor}`,
          borderLeft: `4px solid ${edgeColor}`,
          backgroundColor: theme.white,
          minWidth: 0,
        }}
      >
        <div style={itemBylineRowStyle}>
          <ItemKindTitle>Artwork scan</ItemKindTitle>
          {canShare ? (
            <ShareChoice
              state={dontShare ? "blocked" : shared ? "shared" : "open"}
              loading={isSharing || isUpdatingDontShare}
              onShare={() => void writeShared(true)}
              onUnshare={() => void writeShared(false)}
              onDontShare={() => setDontShareOpen(true)}
              onAllow={() => void writeDontShare(false)}
              shareTestId="sr-class-image-share"
              blockTestId="sr-class-image-dont-share"
              artworkId={artwork.id}
            />
          ) : null}
        </div>
        {dontShare ? (
          <DontShareReasonText reason={artwork.dontShareReason} />
        ) : null}
        {dontShareModal}
        {imageSize ? (
          image(imageSize, true, false, sourceTag)
        ) : (
          <div
            style={{
              position: "relative",
              width: "100%",
              height: 240,
              borderRadius: 8,
              overflow: "hidden",
              backgroundColor: "#1a1a1a",
            }}
          >
            <ArtworkEditableThumb
              studentId={row.studentId}
              artworkId={artwork.id}
              artwork={artwork}
              source={ARTWORK_EDIT_SOURCE.STANDOUT_CLASS_IMAGE}
              fill
              objectFit="contain"
              borderRadius={8}
              alt="Student artwork"
              caption="Student artwork"
              viewLabel="Open class scan"
              testId="sr-class-image-open"
              imageTestId="sr-class-image-img"
              showTileActions={true}
              imageTags={sourceTag}
            />
          </div>
        )}
        <ItemAttribution
          verb="taken by"
          name={asItem.bylineName}
          when={asItem.bylineWhen}
        />
      </div>
    )
  }

  if (identity) {
    const studentName = identity.student?.name?.trim() || row.studentId
    return (
      <div
        data-testid="sr-class-image"
        data-artwork-id={artwork.id}
        data-student-id={row.studentId}
        data-shared={shared ? "true" : "false"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 12,
          borderRadius: 8,
          border: `1px solid ${edgeColor}`,
          borderLeft: `4px solid ${edgeColor}`,
          backgroundColor: theme.gray[1],
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={reviewHalfStyle}>
            <ReviewField>
              {identity.student ? (
                <ClickableStudentName
                  studentId={identity.student.id}
                  label={studentName}
                />
              ) : (
                studentName
              )}
            </ReviewField>
            <ReviewField>{identity.levelLabel ?? ""}</ReviewField>
            <ReviewField>{identity.className}</ReviewField>
            <ReviewField>{identity.teacherName}</ReviewField>
            <div style={reviewStatusStyle}>
              <span
                data-testid={
                  shared ? "sr-class-image-shared" : "sr-class-image-unshared"
                }
                style={{
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                  color: edgeColor,
                }}
              >
                {dontShare ? "Don't share" : shared ? "Shared" : "Not shared"}
              </span>
              {shareSwitch}
            </div>
            {dontShare ? (
              <DontShareReasonText reason={artwork.dontShareReason} />
            ) : null}
            {dontShareModal}
            <SentFamilyNoteList
              notes={identity.sentNotes ?? []}
              markColor={theme.eric.red}
            />
          </div>
          <div style={reviewHalfStyle}>
            <ReviewField>{identity.bylineName}</ReviewField>
            <ReviewField>{identity.bylineWhen}</ReviewField>
            <div style={{ width: "100%", maxWidth: REVIEW_BLOCK }}>
              <ImageSourceTag capturedBy={artwork.capturedBy} />
              {image(REVIEW_BLOCK, true)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (variant === "feed") {
    const caption = artwork.title?.trim()
    return (
      <StandoutFeedRow
        testId="sr-class-image"
        selection={checkbox}
        shared={shared}
        rowProps={{
          "data-artwork-id": artwork.id,
          "data-student-id": row.studentId,
          "data-shared": shared ? "true" : "false",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FeedTypeIcon background={FEED_SCAN_ICON_BG}>
            <CameraOutlined />
          </FeedTypeIcon>
          <Text strong>{authorName || "Class scan"}</Text>
          {roleLabel ? <span style={FEED_ROLE_PILL}>{roleLabel}</span> : null}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(artwork.timeCreated).format("h:mm A")}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            · Photo
          </Text>
          <ImageSourceTag capturedBy={artwork.capturedBy} />
          <span
            data-testid={
              shared ? "sr-class-image-shared" : "sr-class-image-unshared"
            }
            style={
              dontShare
                ? FEED_DONT_SHARE_PILL
                : shared
                  ? FEED_SHARED_PILL
                  : FEED_PENDING_PILL
            }
          >
            {dontShare ? "Don't share" : shared ? "Shared" : "Pending"}
          </span>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {image(84)}
          {caption ? <Text>{caption}</Text> : null}
        </div>
        {dontShare ? (
          <DontShareReasonText reason={artwork.dontShareReason} />
        ) : null}
        {dontShareModal}
      </StandoutFeedRow>
    )
  }

  return (
    <div
      data-testid="sr-class-image"
      data-artwork-id={artwork.id}
      data-student-id={row.studentId}
      data-shared={shared ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${theme.border}`,
        backgroundColor: theme.gray[1],
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {checkbox}
        {image(108)}
        <Text strong>Class image</Text>
        <ImageSourceTag capturedBy={artwork.capturedBy} />
        <Text type="secondary">{artwork.filename || "Scan"}</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {dayjs(artwork.timeCreated).format(STANDARD_DATE_TIME_FORMAT)}
        </Text>
        {shared ? (
          <Tag color="green" data-testid="sr-class-image-shared">
            Shared
          </Tag>
        ) : dontShare ? (
          <Tag color="red" data-testid="sr-class-image-unshared">
            Don't share
          </Tag>
        ) : (
          <Tag data-testid="sr-class-image-unshared">Not shared</Tag>
        )}
        {shareSwitch}
      </div>
      {dontShare ? (
        <DontShareReasonText reason={artwork.dontShareReason} />
      ) : null}
      {dontShareModal}
    </div>
  )
}

const ClassImageReviewList = ({
  classImages,
  selectionFor,
}: {
  classImages: readonly ReviewClassImage[]
  selectionFor?: (row: ReviewClassImage) => ReviewItemSelection | undefined
}) => {
  const ordered = useMemo(
    () => sortBy(classImages, (row) => -(row.artwork.timeCreated ?? 0)),
    [classImages]
  )
  if (ordered.length === 0) return null
  return (
    <div
      data-testid="sr-class-images"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      {ordered.map((row) => (
        <ClassImageReviewRow
          key={`${row.studentId}:${row.artwork.id}`}
          row={row}
          selection={selectionFor?.(row)}
        />
      ))}
    </div>
  )
}

export const ClassImageStudentSections = ({
  classImages,
  studentsById,
}: {
  classImages: readonly ReviewClassImage[]
  studentsById: Record<string, { name?: string } | undefined>
}) => {
  const sections = useMemo(() => {
    const byStudent = new Map<string, ReviewClassImage[]>()
    for (const row of classImages) {
      const group = byStudent.get(row.studentId)
      if (group) group.push(row)
      else byStudent.set(row.studentId, [row])
    }
    return sortBy([...byStudent.entries()], ([studentId]) =>
      (studentsById[studentId]?.name ?? studentId).toLowerCase()
    )
  }, [classImages, studentsById])

  if (sections.length === 0) return null
  return (
    <div
      data-testid="sr-class-image-students"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      {sections.map(([studentId, rows]) => (
        <div
          key={studentId}
          data-testid="sr-class-image-student"
          data-student-id={studentId}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <Text strong>{studentsById[studentId]?.name ?? studentId}</Text>
          <ClassImageReviewList classImages={rows} />
        </div>
      ))}
    </div>
  )
}

export default ClassImageReviewList
